import { AI_FEATURE_HELP_CONTENT } from './aiFeatureHelp';

/**
 * Centralized configuration for all AI features organized by categories
 * Each feature includes all data needed for both display and help modal
 */
export const AI_FEATURE_CATEGORIES = [
  {
    id: 'background',
    title: '🖼️ Sfondo',
    features: [
      {
        id: 'removebg',
        title: 'Rimuovi sfondo',
        image: '/assets/sidebar/ai/rimuovi_sfondo.png',
        prompt: 'REMOVE_BACKGROUND',
        caption: 'Rimuovi sfondo',
        keywords: [
          'scontornare',
          'trasparente',
          'isolare',
          'staccare',
          'sagoma',
          'ritagliare',
          'togliere',
          'rimuovere',
        ],
        ...AI_FEATURE_HELP_CONTENT['removebg'],
      },
      {
        id: 'blurbg',
        title: 'Sfoca sfondo',
        image: '/assets/sidebar/ai/sfoca_sfondo.png',
        prompt: 'BLUR_BACKGROUND',
        caption: 'Sfoca sfondo',
        keywords: ['sfocare', 'sfocatura', 'sfuocato', 'risalto', 'sfumare'],
        ...AI_FEATURE_HELP_CONTENT['blurbg'],
      },
      {
        id: 'dissolvenza',
        title: 'Dissolvenza',
        image: '/assets/sidebar/ai/dissolvenza.png',
        prompt: 'DISSOLVENZA',
        caption: 'Dissolvenza',
        keywords: ['sfumatura', 'sfumare', 'ovale', 'vignetta', 'alone'],
        loadingMessages: [
          { icon: '🖼️', text: 'Analizzando la foto...' },
          { icon: '⭕', text: 'Creando la forma ovale...' },
          { icon: '🌫️', text: 'Applicando la dissolvenza graduale...' },
          { icon: '✨', text: 'Sfumando verso il bianco...' },
          { icon: '🎨', text: 'Perfezionando i bordi morbidi...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['dissolvenza'],
      },
      {
        id: 'expand',
        title: 'Espandi Scena',
        image: '/assets/sidebar/ai/espandi_scena.png',
        prompt: 'EXPAND_IMAGE',
        caption: 'Espandi Scena',
        keywords: [
          'espandere',
          'allargare',
          'margini',
          'spazio',
          'tagliata',
          'stretta',
          'inquadratura',
        ],
        ...AI_FEATURE_HELP_CONTENT['expand'],
      },
      {
        // Special feature: dedicated handler (handleBackgroundFusion), no data-ai-edit.
        id: 'background-fusion',
        title: 'Fusione Sfondo',
        image: '/assets/sidebar/ai/fusione_sfondo.png',
        caption: 'Fusione Sfondo',
        keywords: ['cielo', 'nuvole', 'paradiso', 'fondere', 'ambientare'],
        loadingMessages: [
          { icon: '🖼️', text: 'Analizzando foto e sfondo...' },
          { icon: '🎨', text: 'Fondendo il ritratto nello sfondo...' },
          { icon: '✨', text: 'Armonizzando luci e colori...' },
          { icon: '💎', text: 'Rifinitura finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['background-fusion'],
      },
    ],
  },
  {
    id: 'quality',
    title: '✨ Qualità e Restauro',
    features: [
      {
        id: 'imageEnhancement',
        title: 'Migliora risoluzione',
        image: '/assets/sidebar/ai/migliora_risoluzione.png',
        prompt: 'ENHANCE_IMAGE',
        caption: 'Migliora risoluzione',
        keywords: [
          'risoluzione',
          'sgranata',
          'pixelata',
          'qualità',
          'nitidezza',
          'ingrandire',
          'piccola',
          'rovinata',
          'vecchia',
          'sfocata',
          'bassa',
        ],
        ...AI_FEATURE_HELP_CONTENT['imageEnhancement'],
      },
      {
        id: 'colorizebw',
        title: 'Colora Foto Bianco e Nero',
        image: '/assets/sidebar/ai/colora.png',
        prompt: 'COLORIZE_BW',
        caption: 'Colora Foto Bianco e Nero',
        keywords: [
          'bianco e nero',
          'colorare',
          'colori',
          'antica',
          'seppia',
          'vecchia',
        ],
        ...AI_FEATURE_HELP_CONTENT['colorizebw'],
      },
      {
        id: 'restore-photo',
        title: 'Restaura Foto',
        image: '/assets/sidebar/ai/restaura_foto.png',
        prompt: 'RESTORE_PHOTO',
        caption: 'Restaura Foto',
        keywords: [
          'graffi',
          'rovinata',
          'danneggiata',
          'macchie',
          'strappata',
          'pieghe',
          'vecchia',
          'antica',
          'riparare',
          'aggiustare',
        ],
        loadingMessages: [
          { icon: '📷', text: 'Analizzando la foto danneggiata...' },
          { icon: '🔍', text: 'Identificando i difetti...' },
          { icon: '🔧', text: 'Riparando graffi e macchie...' },
          { icon: '🎨', text: 'Recuperando i colori originali...' },
          { icon: '✨', text: 'Migliorando la nitidezza...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['restore-photo'],
      },
      {
        id: 'remove-document-photo-marks',
        title: 'Ripulisci Foto Documento',
        image: '/assets/sidebar/ai/ripulisci_foto_documento.png',
        prompt: 'RESTORE_ID_PHOTO',
        caption: 'Ripulisci Foto Documento',
        keywords: [
          'timbro',
          'scritte',
          'documento',
          'patente',
          'passaporto',
          'carta identità',
          "carta d'identità",
          'fototessera',
          'filigrana',
        ],
        loadingMessages: [
          { icon: '🪪', text: 'Analizzando la fototessera...' },
          { icon: '🔍', text: 'Identificando timbri e segni...' },
          { icon: '🧹', text: 'Rimuovendo le marcature...' },
          { icon: '🖼️', text: 'Ripulendo lo sfondo...' },
          { icon: '✨', text: 'Migliorando la qualità...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-document-photo-marks'],
      },
    ],
  },
  {
    id: 'corrections',
    title: '🪄 Rimozioni',
    features: [
      {
        id: 'magic-eraser',
        title: 'Pennello correttivo',
        image: '/assets/sidebar/ai/pennello_correttivo.png',
        prompt: 'MAGIC_ERASER',
        caption: 'Pennello correttivo',
        keywords: [
          'cancellare',
          'gomma',
          'eliminare',
          'macchia',
          'scritta',
          'data',
          'togliere',
          'rimuovere',
        ],
        ...AI_FEATURE_HELP_CONTENT['magic-eraser'],
      },
      {
        id: 'remove-hug',
        title: 'Rimuovi Abbraccio',
        image: '/assets/sidebar/ai/togli_abbraccio.png',
        prompt: 'REMOVE_HUG',
        caption: 'Rimuovi Abbraccio',
        keywords: [
          'abbraccio',
          'mano',
          'braccio',
          'spalla',
          'separare',
          'togliere',
          'rimuovere',
        ],
        loadingMessages: [
          { icon: '🤗', text: 'Analizzando la foto...' },
          { icon: '👥', text: "Identificando l'abbraccio..." },
          { icon: '✂️', text: 'Separando le persone...' },
          { icon: '🎨', text: 'Ricostruendo lo sfondo...' },
          { icon: '✨', text: 'Rifinitura dei dettagli...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-hug'],
      },
      {
        id: 'remove-the-person-in-front-of-the-subject',
        title: 'Rimuovi la persona davanti al soggetto',
        image: '/assets/sidebar/ai/rimuovi_persona_davanti.png',
        prompt: 'REMOVE_PERSON_IN_FRONT_OF_SUBJECT',
        caption: 'Rimuovi la persona davanti al soggetto',
        keywords: [
          'persona',
          'bambino',
          'nipote',
          'davanti',
          'coprire',
          'primo piano',
          'togliere',
          'rimuovere',
        ],
        loadingMessages: [
          { icon: '👥', text: 'Analizzando le persone nella foto...' },
          { icon: '🎯', text: 'Identificando il soggetto principale...' },
          { icon: '✂️', text: 'Rimuovendo la persona in primo piano...' },
          { icon: '🎨', text: 'Ricostruendo le parti nascoste...' },
          { icon: '✨', text: 'Perfezionando i dettagli...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-the-person-in-front-of-the-subject'],
      },
      {
        id: 'remove-the-object-in-front-of-the-subject',
        title: 'Rimuovi gli oggetti davanti al soggetto',
        image: '/assets/sidebar/ai/rimuovi_oggetto_davanti.png',
        prompt: 'REMOVE_OBJECT_IN_FRONT_OF_SUBJECT',
        caption: 'Rimuovi gli oggetti davanti al soggetto',
        keywords: [
          'oggetto',
          'bicchiere',
          'microfono',
          'telefono',
          'davanti',
          'ostacolo',
          'togliere',
          'rimuovere',
        ],
        loadingMessages: [
          { icon: '🔍', text: 'Analizzando gli elementi nella foto...' },
          { icon: '🎯', text: 'Identificando il soggetto principale...' },
          { icon: '✂️', text: 'Rimuovendo gli oggetti in primo piano...' },
          { icon: '🎨', text: 'Ricostruendo le parti nascoste...' },
          { icon: '✨', text: 'Perfezionando i dettagli...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-the-object-in-front-of-the-subject'],
      },
      {
        id: 'remove-glasses',
        title: 'Rimuovi Occhiali',
        image: '/assets/sidebar/ai/rimuovi_occhiali.png',
        prompt: 'REMOVE_GLASSES',
        caption: 'Rimuovi Occhiali',
        keywords: ['occhiali', 'lenti', 'montatura', 'togliere', 'rimuovere'],
        loadingMessages: [
          { icon: '👓', text: 'Analizzando la foto...' },
          { icon: '🔍', text: 'Identificando gli occhiali...' },
          { icon: '✂️', text: 'Rimuovendo la montatura...' },
          { icon: '👁️', text: 'Ricostruendo il viso...' },
          { icon: '✨', text: 'Perfezionando i dettagli...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-glasses'],
      },
      {
        id: 'remove-glasses-glare',
        title: 'Rimuovi Riflesso dagli Occhiali',
        image: '/assets/sidebar/ai/rimuovi_riflesso_occhiali.png',
        prompt: 'REMOVE_GLASSES_GLARE',
        caption: 'Rimuovi Riflesso dagli Occhiali',
        keywords: [
          'riflesso',
          'riflessi',
          'bagliore',
          'luce',
          'lenti',
          'togliere',
          'rimuovere',
        ],
        loadingMessages: [
          { icon: '👓', text: 'Analizzando gli occhiali...' },
          { icon: '✨', text: 'Identificando i riflessi...' },
          { icon: '🔧', text: 'Rimuovendo i bagliori...' },
          { icon: '👁️', text: 'Recuperando i dettagli...' },
          { icon: '🎨', text: 'Migliorando la trasparenza...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        ...AI_FEATURE_HELP_CONTENT['remove-glasses-glare'],
      },
    ],
  },
  {
    id: 'face',
    title: '💄 Ritratto e Viso',
    features: [
      {
        id: 'correct-posture',
        title: 'Correggi postura',
        image: '/assets/sidebar/ai/correggi_postura.png',
        prompt: 'CORRECT_POSTURE',
        caption: 'Correggi postura',
        keywords: [
          'postura',
          'testa',
          'storta',
          'storto',
          'inclinata',
          'inclinato',
          'piegata',
          'piegato',
          'raddrizzare',
          'posa',
          'appoggiata',
          'appoggiato',
        ],
        loadingMessages: [
          { icon: '🔍', text: 'Analizzando la postura...' },
          { icon: '🎯', text: 'Individuando allineamento di volto e collo...' },
          { icon: '🧍', text: 'Raddrizzando la posa...' },
          { icon: '🎨', text: 'Ricostruendo collo, capelli e sfondo...' },
          { icon: '✨', text: 'Preservando espressione e identità...' },
          { icon: '💎', text: 'Controllo qualità finale...' },
        ],
        loaderDurationMs: 60000,
        ...AI_FEATURE_HELP_CONTENT['correct-posture'],
      },
      {
        id: 'smile',
        title: 'Sorriso',
        image: '/assets/sidebar/ai/sorriso.png',
        prompt: 'ADD_SMILE',
        caption: 'Sorriso',
        keywords: [
          'sorriso',
          'sorridere',
          'sorridente',
          'serio',
          'seria',
          'triste',
          'espressione',
          'bocca',
        ],
        ...AI_FEATURE_HELP_CONTENT['smile'],
      },
      {
        id: 'makeLookYounger',
        title: 'Filtro bellezza',
        image: '/assets/sidebar/ai/filtro_bellezza.png',
        prompt: 'MAKE_LOOK_YOUNGER',
        caption: 'Filtro bellezza',
        keywords: [
          'giovane',
          'ringiovanire',
          'rughe',
          'bellezza',
          'pelle',
          'trucco',
        ],
        ...AI_FEATURE_HELP_CONTENT['makeLookYounger'],
      },
      {
        id: 'faceEnhancement',
        title: 'Migliora viso',
        image: '/assets/sidebar/ai/migliora_risoluzione.png',
        prompt: 'ENHANCE_FACE',
        caption: 'Migliora viso',
        keywords: [
          'viso',
          'volto',
          'faccia',
          'sfocato',
          'sfocata',
          'nitido',
          'dettagli',
        ],
        ...AI_FEATURE_HELP_CONTENT['faceEnhancement'],
      },
      {
        id: 'change-clothes',
        title: 'Cambia abito',
        image: '/assets/sidebar/ai/cambio_abito.png',
        prompt: 'CHANGE_CLOTHES',
        caption: 'Cambia abito',
        keywords: [
          'abito',
          'abiti',
          'vestito',
          'vestiti',
          'giacca',
          'cravatta',
          'camicia',
          'elegante',
          'divisa',
        ],
        ...AI_FEATURE_HELP_CONTENT['change-clothes'],
      },
    ],
  },
];

/**
 * Default loading messages for AI features
 */
export const DEFAULT_AI_LOADING_MESSAGES = [
  { icon: '🔍', text: 'Analizzando la foto...' },
  { icon: '🤖', text: 'Elaborando con intelligenza artificiale...' },
  { icon: '🎨', text: 'Applicando le modifiche richieste...' },
  { icon: '✨', text: 'Ottimizzando i risultati...' },
  { icon: '🎯', text: 'Perfezionando i dettagli...' },
  { icon: '💎', text: 'Controllo qualità finale...' },
];

/**
 * Get all features as a flat array (for backward compatibility)
 */
export function getAllFeatures() {
  return AI_FEATURE_CATEGORIES.reduce((acc, category) => {
    return acc.concat(category.features);
  }, []);
}

/**
 * Get a specific feature by ID
 */
export function getFeatureById(featureId) {
  for (const category of AI_FEATURE_CATEGORIES) {
    const feature = category.features.find((f) => f.id === featureId);
    if (feature) return feature;
  }
  return null;
}

/**
 * Get a specific category by ID
 */
export function getCategoryById(categoryId) {
  return AI_FEATURE_CATEGORIES.find((cat) => cat.id === categoryId);
}

/**
 * Get feature count for a category
 */
export function getCategoryFeatureCount(categoryId) {
  const category = getCategoryById(categoryId);
  return category ? category.features.length : 0;
}
